"use client";

import { useDefaultCountry } from "@pex/ui-web/components/default-country-provider";
import { Button } from "@pex/ui-web/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@pex/ui-web/components/ui/dialog";
import { Input } from "@pex/ui-web/components/ui/input";
import { Label } from "@pex/ui-web/components/ui/label";
import { PhoneField } from "@pex/ui-web/components/ui/phone-field";
import { isValidPhoneNumber } from "libphonenumber-js";
import { useState } from "react";
import { isValidEmail } from "@/lib/validation";

interface Props {
  onOpenChange: (open: boolean) => void;
  open: boolean;
  programmeLocationId: string;
}

type Status = "idle" | "loading" | "success" | "error";

interface FormState {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
}

interface FormErrors {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

const EMPTY_FORM: FormState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
};

function validate(form: FormState): FormErrors {
  const errors: FormErrors = {};
  if (!form.firstName.trim()) {
    errors.firstName = "Please enter your first name.";
  }
  if (!form.lastName.trim()) {
    errors.lastName = "Please enter your last name.";
  }
  if (!isValidEmail(form.email)) {
    errors.email = "Please enter a valid email address.";
  }
  if (!isValidPhoneNumber(form.phone)) {
    errors.phone = "Please enter a valid phone number.";
  }
  return errors;
}

export function JoinRequestModal({
  open,
  onOpenChange,
  programmeLocationId,
}: Props) {
  const defaultCountry = useDefaultCountry();
  const [status, setStatus] = useState<Status>("idle");
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setStatus("idle");
      setForm(EMPTY_FORM);
      setErrors({});
    }
    onOpenChange(nextOpen);
  }

  function updateField(field: keyof FormState) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    };
  }

  function updatePhone(value: string) {
    setForm((prev) => ({ ...prev, phone: value }));
    setErrors((prev) => ({ ...prev, phone: undefined }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const fieldErrors = validate(form);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }
    setStatus("loading");
    try {
      const response = await fetch("/api/join-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone,
          programmeLocationId,
        }),
      });
      if (response.ok) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  }

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent>
        {status !== "success" && (
          <DialogHeader>
            <DialogTitle>Request to Join</DialogTitle>
            <DialogDescription>
              Fill in your details and we'll pass them on to the programme
              facilitator.
            </DialogDescription>
          </DialogHeader>
        )}

        {status === "success" ? (
          <div className="py-4 text-center">
            <p className="font-semibold text-foreground">Request sent!</p>
            <p className="mt-1 text-muted-foreground text-sm">
              The facilitator will be in touch with you soon.
            </p>
            <Button className="mt-4" onClick={() => handleOpenChange(false)}>
              Close
            </Button>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First name</Label>
                <Input
                  id="firstName"
                  maxLength={50}
                  onChange={updateField("firstName")}
                  placeholder="Jane"
                  value={form.firstName}
                />
                {errors.firstName && (
                  <p className="text-destructive text-sm">{errors.firstName}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  id="lastName"
                  maxLength={50}
                  onChange={updateField("lastName")}
                  placeholder="Smith"
                  value={form.lastName}
                />
                {errors.lastName && (
                  <p className="text-destructive text-sm">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                inputMode="email"
                onChange={updateField("email")}
                placeholder="jane@example.com"
                type="email"
                value={form.email}
              />
              {errors.email && (
                <p className="text-destructive text-sm">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <PhoneField
                defaultCountry={defaultCountry}
                id="phone"
                onChange={updatePhone}
                value={form.phone}
              />
              {errors.phone && (
                <p className="text-destructive text-sm">{errors.phone}</p>
              )}
            </div>

            {status === "error" && (
              <p className="text-destructive text-sm">
                Something went wrong. Please try again.
              </p>
            )}

            <div className="flex gap-3">
              <Button
                className="flex-1"
                disabled={status === "loading"}
                type="submit"
              >
                {status === "loading" ? "Sending…" : "Send Request"}
              </Button>
              <Button
                onClick={() => handleOpenChange(false)}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
